import React from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Button, HStack, useColorModeValue, Badge, Text, Flex, Icon, VStack, Stack, Box, Heading
} from '@chakra-ui/react';
import { CheckIcon, WarningIcon, TimeIcon, QuestionIcon } from '@chakra-ui/icons';
import { FiMessageCircle } from 'react-icons/fi';

/**
 * Detail Item Component for Modal
 */
const DetailItem = ({ label, value }) => (
  <Box mb={2}>
    <Text fontWeight="bold" fontSize="sm" color="gray.500">
      {label}
    </Text>
    <Text>{value}</Text>
  </Box>
);

/**
 * Status Badge Component
 */
const StatusBadge = ({ isCompleted, isInProgress, isPendingConfirmation }) => {
  let colorScheme, status, icon;

  if (isCompleted) {
    colorScheme = 'green';
    status = 'Concluído';
    icon = CheckIcon;
  } else if (isPendingConfirmation) {
    colorScheme = 'orange';
    status = 'Aguardando Confirmação';
    icon = QuestionIcon;
  } else if (isInProgress) {
    colorScheme = 'yellow';
    status = 'Em Andamento';
    icon = TimeIcon;
  } else {
    colorScheme = 'red';
    status = 'Aguardando';
    icon = WarningIcon;
  }

  return (
    <Badge colorScheme={colorScheme} px={2} py={1} borderRadius="full">
      <Flex align="center">
        <Icon as={icon} mr={1} boxSize="10px" />
        <Text>{status}</Text>
      </Flex>
    </Badge>
  );
};

/**
 * Helper to format currency values safely
 */
const formatCurrency = (value) => {
  if (value === undefined || value === null) return 'N/A';
  
  if (typeof value === 'number') {
    return `R$ ${value.toFixed(2)}`;
  }
  
  // Try to convert to number if it's a string that looks like a number
  if (typeof value === 'string' && !isNaN(parseFloat(value))) {
    return `R$ ${parseFloat(value).toFixed(2)}`;
  }
  
  // If it's a string but not convertible to number, just return it with currency symbol
  if (typeof value === 'string') {
    return `R$ ${value}`;
  }
  
  return 'N/A';
};

/**
 * RejectDetailsModal Component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.rejectionData - Data for the rejection confirmation
 * @param {Function} props.onActionTaken - Function to handle action taken
 * @param {Function} props.onOpenChat - Function to open the chat
 */
const RejectDetailsModal = ({ isOpen, onClose, rejectionData, onActionTaken, onOpenChat }) => {
  // Colors
  const modalHeaderBg = useColorModeValue('blue.50', 'blue.900');
  const modalHeaderColor = useColorModeValue('blue.700', 'blue.100');
  const mainBoxBg = useColorModeValue('gray.50', 'gray.700');
  const reasonBoxBg = useColorModeValue('red.50', 'red.900');
  const serviceBoxBg = useColorModeValue('blue.50', 'blue.900');
  const workerBoxBg = useColorModeValue('purple.50', 'purple.900');
  const userBoxBg = useColorModeValue('teal.50', 'teal.900');
  const filterBorderColor = useColorModeValue('gray.200', 'gray.600');

  if (!rejectionData) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="md">
        <ModalHeader 
          bg={modalHeaderBg}
          color={modalHeaderColor}
          borderTopRadius="md"
          py={4}
        >
          <HStack>
            <Text>Detalhes da Recusa de Serviço</Text>
            <StatusBadge
              isCompleted={rejectionData.actionTaken}
              isInProgress={rejectionData.inProgress}
              isPendingConfirmation={rejectionData.pendingConfirmation}
            />
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={4}>
          <VStack align="stretch" spacing={4}>
            {/* Main Information */}
            <Box 
              p={4} 
              bg={mainBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="blue.500">
                Informações Principais
              </Heading>
              <Stack spacing={3}>
                <Flex flexWrap="wrap" justify="space-between">
                  <DetailItem label="ID" value={rejectionData._id || 'N/A'} />
                  <DetailItem 
                    label="Data de Criação" 
                    value={
                      rejectionData.createdAt
                        ? new Date(rejectionData.createdAt).toLocaleString()
                        : 'N/A'
                    } 
                  />
                </Flex>
              </Stack>
            </Box>
            
            {/* Reason Box */}
            <Box 
              p={4} 
              bg={reasonBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="red.500">
                Razão da Recusa
              </Heading>
              <DetailItem label="Motivo" value={rejectionData.reason || 'N/A'} />
            </Box>
            
            {/* Service Details */}
            <Box 
              p={4} 
              bg={serviceBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="blue.500">
                Detalhes do Serviço
              </Heading>
              <Stack spacing={3}>
                <DetailItem 
                  label="Descrição" 
                  value={rejectionData.serviceId?.description || 'N/A'} 
                />
                <Flex flexWrap="wrap" justify="space-between">
                  <DetailItem 
                    label="Status" 
                    value={rejectionData.serviceId?.status || 'N/A'} 
                  />
                  <DetailItem 
                    label="Valor" 
                    value={formatCurrency(rejectionData.serviceId?.value)} 
                  />
                </Flex>
                <Flex flexWrap="wrap" justify="space-between">
                  <DetailItem 
                    label="Data de Início" 
                    value={rejectionData.serviceId?.startDate 
                      ? new Date(rejectionData.serviceId.startDate).toLocaleDateString()
                      : 'N/A'
                    } 
                  />
                  <DetailItem 
                    label="Data de Término" 
                    value={rejectionData.serviceId?.endDate 
                      ? new Date(rejectionData.serviceId.endDate).toLocaleDateString()
                      : 'N/A'
                    } 
                  />
                </Flex>
              </Stack>
            </Box>
            
            {/* User Information */}
            <Box 
              p={4} 
              bg={userBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="teal.500">
                Informações do Usuário
              </Heading>
              <Stack spacing={3}>
                <DetailItem 
                  label="Nome" 
                  value={rejectionData.userId?.name || 'N/A'} 
                />
                <Flex flexWrap="wrap" justify="space-between">
                  <DetailItem 
                    label="Email" 
                    value={rejectionData.userId?.email || 'N/A'} 
                  />
                  <DetailItem 
                    label="Telefone" 
                    value={rejectionData.userId?.phone || 'N/A'} 
                  />
                </Flex>
              </Stack>
            </Box>
            
            {/* Worker Information (if available) */}
            {rejectionData.serviceId?.workerId && (
              <Box 
                p={4} 
                bg={workerBoxBg} 
                borderRadius="md"
                borderWidth="1px"
                borderColor={filterBorderColor}
              >
                <Heading as="h3" size="sm" mb={3} color="purple.500">
                  Informações do Trabalhador
                </Heading>
                <Stack spacing={3}>
                  <DetailItem 
                    label="Nome" 
                    value={rejectionData.serviceId.workerId?.name || 'N/A'} 
                  />
                  <Flex flexWrap="wrap" justify="space-between">
                    <DetailItem 
                      label="Email" 
                      value={rejectionData.serviceId.workerId?.email || 'N/A'} 
                    />
                    <DetailItem 
                      label="Telefone" 
                      value={rejectionData.serviceId.workerId?.phone || 'N/A'} 
                    />
                  </Flex>
                </Stack>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px" borderColor={filterBorderColor}>
          <HStack spacing={3}>
            {rejectionData.inProgress && !rejectionData.actionTaken && !rejectionData.pendingConfirmation && (
              <Button
                leftIcon={<CheckIcon />}
                colorScheme="green"
                onClick={() => onActionTaken(rejectionData._id)}
              >
                Finalizar
              </Button>
            )}
            {rejectionData.pendingConfirmation && !rejectionData.actionTaken && (
              <Badge colorScheme="orange" px={3} py={2} borderRadius="md">
                <Flex align="center">
                  <Icon as={QuestionIcon} mr={2} />
                  <Text>Aguardando resposta do usuário</Text>
                </Flex>
              </Badge>
            )}
            <Button
              leftIcon={<FiMessageCircle />}
              colorScheme="teal"
              variant="outline"
              onClick={() => onOpenChat(rejectionData.userId?._id || rejectionData.userId)}
            >
              Chat com Usuário
            </Button>
            <Button onClick={onClose} variant="ghost">
              Fechar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RejectDetailsModal;
